import { Link } from 'react-router-dom';
import { ChevronUp } from 'lucide-react';

import { TimeAgo } from './TimeAgo';
import {
  displaySpaceName,
  initialsOf,
  isHotThread,
  isUnanswered,
  threadExcerpt,
  threadTags,
} from '../forum-display';
import type { PostListItem, Subforum } from '../types';

/**
 * One thread in a dense list.
 *
 * Ported from the admin app's redesign (ai2aim-ems 3bb83bc) and re-expressed in
 * canvas tokens. The structure is theirs — score column, title with an earned
 * badge, excerpt, one meta line — because the index, a space page and the
 * archive all render this, and three surfaces drawing a thread differently is
 * how they drift apart.
 *
 * TWO DELIBERATE INHERITANCES from that design:
 *
 * The score is READ ONLY. Voting belongs on the thread itself, where the
 * reader can see what they are voting on; a vote control in a list invites a
 * verdict on a headline.
 *
 * A view count is OMITTED when the endpoint did not project one, rather than
 * printed as zero. The public list currently returns no views, and the page
 * this replaced rendered "0 views" under every thread — a measurement the site
 * was not making, stated as a fact.
 *
 * One departure: the badge. That design tints Hot orange, and this canvas is
 * single-accent by decision, so hot takes the accent and unanswered stays on
 * ink. Two states, two treatments, no hue invented for one chip.
 */
interface ThreadRowProps {
  /**
   * A list row. The excerpt and tags are the API's (`excerpt`, `tags`) when the
   * row carries them, else worked out from `content` on an older API build.
   */
  post: PostListItem;
  /** Undefined when the space is not loaded; the pill is omitted, not blanked. */
  space?: Subforum;
  /**
   * `index` leads the meta line with the space, because that list spans all of
   * them. `space` is already inside one, so it leads with the author.
   */
  variant?: 'index' | 'space';
  className?: string;
}

export function ThreadRow({
  post,
  space,
  variant = 'index',
  className = '',
}: ThreadRowProps) {
  const hot = isHotThread(post);
  const tags = threadTags(post);
  const excerpt = threadExcerpt(post);
  const lit = post.upvotes >= 5;

  return (
    <Link
      to={`/forum/post/${post.id}`}
      className={`dc-thread-row flex gap-3.5 px-5 py-4 ${className}`}
      style={{ borderTop: '1px solid rgba(var(--ink-rgb),.13)' }}
    >
      <div className="flex w-[42px] flex-none flex-col items-center gap-0.5 pt-0.5">
        <ChevronUp
          size={14}
          strokeWidth={2.4}
          style={{ color: lit ? 'var(--acc2)' : 'rgba(var(--ink-rgb),.62)' }}
        />
        <span
          className="text-xs font-bold leading-tight"
          style={{ color: lit ? 'var(--acc2)' : 'rgba(var(--ink-rgb),.62)' }}
        >
          {post.upvotes}
        </span>
        <span
          className="text-[10px] font-medium uppercase tracking-wider"
          style={{ color: 'rgba(var(--ink-rgb),.62)' }}
        >
          {post.upvotes === 1 ? 'vote' : 'votes'}
        </span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-[7px]">
        <div className="flex items-start gap-2">
          <span
            className="flex-1 text-sm font-semibold leading-[1.35]"
            style={{ color: 'var(--ink)', textWrap: 'pretty' }}
          >
            {post.title}
          </span>
          {hot ? (
            <span
              className="flex-none rounded-[5px] px-2 py-0.5 text-[10px] font-bold uppercase leading-[1.6] tracking-wider"
              style={{
                background: 'rgba(var(--acc2-rgb),.12)',
                color: 'var(--acc2)',
              }}
            >
              Hot
            </span>
          ) : isUnanswered(post) ? (
            <span
              className="flex-none rounded-[5px] px-2 py-0.5 text-[10px] font-bold uppercase leading-[1.6] tracking-wider"
              style={{
                background: 'rgba(var(--ink-rgb),.06)',
                color: 'rgba(var(--ink-rgb),.62)',
              }}
            >
              Unanswered
            </span>
          ) : null}
        </div>

        {excerpt && (
          <p
            className="line-clamp-2 text-xs leading-[1.55]"
            style={{ color: 'rgba(var(--ink-rgb),.62)', textWrap: 'pretty' }}
          >
            {excerpt}
          </p>
        )}

        <div
          className="flex flex-wrap items-center gap-2.5 text-[11px] font-medium"
          style={{ color: 'rgba(var(--ink-rgb),.62)' }}
        >
          {variant === 'index' ? (
            <>
              {space && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full py-0.5 pl-0.5 pr-2.5"
                  style={{ background: 'rgba(var(--ink-rgb),.06)' }}
                >
                  <span
                    className="grid size-[18px] place-items-center rounded-full text-[8px] font-bold"
                    style={{
                      background: 'rgba(var(--acc2-rgb),.12)',
                      color: 'var(--acc2)',
                    }}
                  >
                    {initialsOf(space.name)}
                  </span>
                  {displaySpaceName(space.name)}
                </span>
              )}
              <span className="truncate">{post.author}</span>
            </>
          ) : (
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <span
                className="grid size-[18px] flex-none place-items-center rounded-full text-[8px] font-bold"
                style={{
                  background: 'rgba(var(--ink-rgb),.08)',
                  color: 'rgba(var(--ink-rgb),.62)',
                }}
              >
                {initialsOf(post.author)}
              </span>
              <span className="truncate">{post.author}</span>
            </span>
          )}

          <span
            className="size-[3px] flex-none rounded-full"
            style={{ background: 'rgba(var(--ink-rgb),.4)' }}
          />
          <span>
            {post.commentCount} {post.commentCount === 1 ? 'reply' : 'replies'}
          </span>

          {typeof post.views === 'number' && (
            <>
              <span
                className="size-[3px] flex-none rounded-full"
                style={{ background: 'rgba(var(--ink-rgb),.4)' }}
              />
              <span>{post.views} views</span>
            </>
          )}

          {tags.length > 0 && (
            <span className="flex flex-wrap gap-1.5">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-[6px] px-[7px] py-0.5 text-[11px] font-medium leading-[1.4]"
                  style={{ background: 'rgba(var(--ink-rgb),.06)' }}
                >
                  {tag}
                </span>
              ))}
            </span>
          )}

          <TimeAgo timestamp={post.createdAt} className="ml-auto flex-none" />
        </div>
      </div>
    </Link>
  );
}
